import React, { useMemo } from 'react'
import { observer } from 'mobx-react'
import { Alert, Box, Slider, Typography } from '@mui/material'

import { useStores } from 'admin/hooks'

const MAX_POINTS = 5000
const GRID_DECIMALS = 4

const getLatLng = (point) => {
  const lat = Number(point.routeCentroidLat)
  const lng = Number(point.routeCentroidLng)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const DriverRouteHeatMap = () => {
  const { reportStore } = useStores()

  const [radius, setRadius] = React.useState(16)
  const [opacity, setOpacity] = React.useState(0.48)

  const model = useMemo(() => {
    const source = reportStore.driverRouteFilteredRows || []
    const aggregated = new Map()

    source.forEach((row) => {
      const point = getLatLng(row)
      if (!point) return
      const key = `${point.lat.toFixed(GRID_DECIMALS)}:${point.lng.toFixed(GRID_DECIMALS)}`
      const previous = aggregated.get(key)
      if (previous) {
        previous.weight += 1
      } else {
        aggregated.set(key, { ...point, weight: 1 })
      }
    })

    const points = Array.from(aggregated.values())
    if (!points.length) return { points: [], totalPoints: 0, isDownsampled: false }

    const totalPoints = points.length
    const isDownsampled = totalPoints > MAX_POINTS
    const sampled = isDownsampled
      ? points.filter((_, index) => index % Math.ceil(totalPoints / MAX_POINTS) === 0)
      : points

    const bounds = sampled.reduce(
      (acc, point) => {
        acc.minLat = Math.min(acc.minLat, point.lat)
        acc.maxLat = Math.max(acc.maxLat, point.lat)
        acc.minLng = Math.min(acc.minLng, point.lng)
        acc.maxLng = Math.max(acc.maxLng, point.lng)
        return acc
      },
      { minLat: 999, maxLat: -999, minLng: 999, maxLng: -999 },
    )

    const latSpan = Math.max(0.00001, bounds.maxLat - bounds.minLat)
    const lngSpan = Math.max(0.00001, bounds.maxLng - bounds.minLng)

    return {
      points: sampled.map((point) => ({
        ...point,
        id: `${point.lat.toFixed(GRID_DECIMALS)}:${point.lng.toFixed(GRID_DECIMALS)}`,
        x: ((point.lng - bounds.minLng) / lngSpan) * 100,
        y: 100 - ((point.lat - bounds.minLat) / latSpan) * 100,
      })),
      totalPoints,
      isDownsampled,
    }
  }, [reportStore.driverRouteFilteredRows])

  if (!reportStore.driverRouteHeatmapEnabled) return null

  if (!model.points.length) {
    return (
      <Alert severity="info">
        Heat map needs route centroid coordinates from route analysis data.
      </Alert>
    )
  }

  return (
    <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mb: 1 }}
      >
        Route activity heat map
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 1, color: 'text.secondary' }}
      >
        Hotter = more frequent presence/activity.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
        <Box sx={{ width: 180 }}>
          <Typography variant="caption">Radius</Typography>
          <Slider
            min={8}
            max={28}
            step={1}
            value={radius}
            onChange={(_, v) => setRadius(v)}
          />
        </Box>
        <Box sx={{ width: 180 }}>
          <Typography variant="caption">Opacity</Typography>
          <Slider
            min={0.2}
            max={0.8}
            step={0.02}
            value={opacity}
            onChange={(_, v) => setOpacity(v)}
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: 280,
          borderRadius: 1,
          bgcolor: '#fafafa',
          overflow: 'hidden',
        }}
      >
        <svg
          viewBox="0 0 100 100"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
        >
          <polyline
            points={model.points.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke="#1565c0"
            strokeWidth="0.35"
            opacity="0.85"
          />
          {model.points.map((point) => (
            <circle
              key={point.id}
              cx={point.x}
              cy={point.y}
              r={(radius / 10) * Math.sqrt(point.weight)}
              fill="rgb(244,67,54)"
              opacity={Math.min(0.95, opacity * (0.6 + point.weight * 0.1))}
            />
          ))}
        </svg>
      </Box>

      <Typography
        variant="caption"
        sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
      >
        Showing {model.points.length} aggregated points
        {model.isDownsampled ? ` (downsampled from ${model.totalPoints})` : ''}.
      </Typography>
    </Box>
  )
}

export default observer(DriverRouteHeatMap)
